import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Camera, Sparkles } from "lucide-react";
import { SnapToPriceModal } from "./SnapToPriceModal";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useMobileNavVisible } from "@/hooks/useMobileNavVisible";

interface SnapToPriceButtonProps {
  variant?: "default" | "outline" | "ghost" | "fab";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
  showLabel?: boolean;
}

export function SnapToPriceButton({ 
  variant = "default", 
  size = "default",
  className,
  showLabel = true 
}: SnapToPriceButtonProps) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const bottomNavVisible = useMobileNavVisible();

  // Hide FAB only when the mobile bottom nav (with its Scan button) is visible.
  // On mobile routes without the bottom nav (logged out, /auth, admin/brand/vault-partner),
  // the FAB becomes the camera entry point.
  if (variant === "fab" && bottomNavVisible) {
    return null;
  }

  if (variant === "fab") {
    // Position bottom-right on mobile (above safe area), bottom-left on larger screens.
    const positionClasses = isMobile
      ? "bottom-6 right-4"
      : "bottom-6 left-6";

    return (
      <>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => setOpen(true)}
              className={cn(
                "fixed z-50 flex items-center justify-center",
                positionClasses,
                "h-14 w-14 rounded-full shadow-2xl",
                "bg-primary text-primary-foreground",
                "ring-2 ring-primary/30",
                "hover:shadow-xl hover:scale-105 active:scale-95",
                "transition-all duration-200",
                className
              )}
              style={{ marginBottom: "env(safe-area-inset-bottom, 0px)" }}
              aria-label="Snap to Price - Get instant AI valuation"
            >
              <Camera className="h-6 w-6" />
            </button>
          </TooltipTrigger>
          <TooltipContent side={isMobile ? "left" : "right"} className="bg-primary text-primary-foreground border-primary/50">
            <p className="font-medium">Snap to Price</p>
            <p className="text-xs opacity-80">Get instant AI valuation</p>
          </TooltipContent>
        </Tooltip>
        <SnapToPriceModal open={open} onOpenChange={setOpen} />
      </>
    );
  }

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={() => setOpen(true)}
        className={cn("gap-2", className)}
      >
        <Sparkles className="h-4 w-4" />
        {showLabel && "Snap to Price"}
      </Button>
      <SnapToPriceModal open={open} onOpenChange={setOpen} />
    </>
  );
}
